import React, { useEffect, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Tag } from "antd";
import {
  createFormInitialValue,
  createTitle,
  createShema,
  joiResolver,
} from "./SingleOrder.utils";
import { SingleOrderForm } from "../single-order-form/SingleOrderForm";
import { SingleOrderHeader } from "../../molecules/";
import { SingleOrderContextProvider } from "../../organisms";
import {
  SingleOrderWrapper,
  SingleOrderFormWrapper,
  AccordionHead,
  AccordionTitle,
  AccordionChevron,
  AccordionLabels,
  AccordionActions,
} from "./SingleOrder.styled";
import { useT } from "../../../i18n";

export const SingleOrder: React.FC<{
  item: any;
  id: any;
  model: any;
  labels: any;
  title_fields: any;
  delete_permission: any;
  background_color?: any;
  delete_url?: string;
  patch_url?: string;
}> = ({
  item,
  id,
  labels,
  title_fields,
  delete_permission,
  background_color,
  model,
  delete_url,
  patch_url,
}) => {
    const t = useT();

    const title = useMemo(() => createTitle(title_fields, item), [
      title_fields,
      item,
    ]);

    const [open, setOpen] = useState(() => String(title ?? "").trim() === "");

    const schema = createShema(item);
    const methods = useForm({
      resolver: joiResolver(schema),
    });

    useEffect(() => {
      methods.reset(createFormInitialValue(item));
      return;
    }, []);

    return (
      <SingleOrderWrapper>
        <SingleOrderContextProvider disableReadMode={false} orderId={id} patchUrl={patch_url} dataType={model?.data_type} itemModelId={model?.id}>
          <AccordionHead
            $open={open}
            $background_color={background_color}
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            aria-label={open ? t("record.collapse") : t("record.expand")}
          >
            <AccordionChevron $open={open}>▶</AccordionChevron>
            <AccordionTitle>{title}</AccordionTitle>
            {/* stopPropagation keeps label clicks/drags from toggling the
                accordion so the label text stays selectable. */}
            <AccordionLabels
              onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {labels?.map((e: any) => (
                <Tag color={e?.color} key={e?.text} style={{ marginInlineEnd: 0 }}>
                  {e?.text}
                </Tag>
              ))}
            </AccordionLabels>
            {open && (
              // Record actions live in the head; stopPropagation keeps them
              // from toggling the accordion.
              <AccordionActions onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <SingleOrderHeader
                  title={title}
                  labels={labels}
                  id={id}
                  delete_permission={delete_permission}
                  delete_url={delete_url}
                  item={item}
                  hideSummary
                />
              </AccordionActions>
            )}
          </AccordionHead>
          {open && (
            <FormProvider {...methods}>
              <SingleOrderFormWrapper $background_color={background_color}>
                <SingleOrderForm controls={item} id={id} patch_url={patch_url} />
              </SingleOrderFormWrapper>
            </FormProvider>
          )}
        </SingleOrderContextProvider>
      </SingleOrderWrapper>
    );
  };
